/**
 * useBRRFormData Hook
 * Manages loading and state for BRR form auto-population data from Neo4j
 */

import { useState, useEffect, useCallback } from 'react';
import { BRRApiClient } from '../services';
import { BRRFormDataResponse } from '../services/types';

interface UseBRRFormDataOptions {
  inspectionLotId: string | null;
  productName?: string;
  sessionId?: string | null;
  selectedLotIds?: string[];
  apiClient: BRRApiClient;
  autoLoad?: boolean;
}

interface UseBRRFormDataReturn {
  data: BRRFormDataResponse | null;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

/**
 * Hook for loading BRR form auto-population data
 *
 * @example
 * const { data, loading, error, reload } = useBRRFormData({
 *   inspectionLotId: '100000001',
 *   productName: 'Paracetamol 500mg',
 *   sessionId: 'abc-123',
 *   apiClient: brrApi,
 *   autoLoad: true
 * });
 */
export const useBRRFormData = ({
  inspectionLotId,
  productName,
  sessionId,
  selectedLotIds,
  apiClient,
  autoLoad = true
}: UseBRRFormDataOptions): UseBRRFormDataReturn => {
  const [data, setData] = useState<BRRFormDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!inspectionLotId) {
      setError(new Error('No inspection lot ID provided'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = await apiClient.getFormData(inspectionLotId, productName, sessionId || undefined, selectedLotIds);
      setData(formData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('Failed to load BRR form data:', error);
    } finally {
      setLoading(false);
    }
  }, [inspectionLotId, productName, sessionId, selectedLotIds, apiClient]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  // Auto-load on mount or when inspectionLotId changes
  useEffect(() => {
    if (autoLoad && inspectionLotId) {
      void load();
    }
  }, [inspectionLotId, productName, sessionId, autoLoad, load]);

  return {
    data,
    loading,
    error,
    reload
  };
};
